// MarketTrackDetailPanel — track-first detail view. Top: identity + thesis +
// market relevance. Middle: latest evidence. Then: a click-to-inspect timeline
// driven by marketTrackWeeklyHistoryByTrackId. Then: sub-patterns and adjacent
// tracks. Weekly signals (the old underlying objects) appear here as evidence,
// not as the primary product layer.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { sortHistoryAsc, lifecycleTone, visibilityTone, evidenceQualityLabelForTrack, marketZoneForTrack, primaryRoleChipForTrack, weeklyDynamicsFor } from "../../lib/tracks";
import { collectSourceLists, parseDistribution, resolveMovementLists, readLatestWeeklyEvidence, pickLatestEvidenceSources, summaryAppearsComplete } from "../../lib/signals/evidence";
import RankedList from "./RankedList";
import LatestMovement from "./LatestMovement";
import EvidenceChips from "./EvidenceChips";
import TrackComposition from "./TrackComposition";
import { trackEvent } from "../../lib/analytics";

const Badge = ({ tone = "muted", children, title }) => (
    <span title={title} className={`badge ${tone === "accent" ? "badge-accent" : tone === "positive" ? "badge-positive" : tone === "warning" ? "badge-warning" : tone === "negative" ? "badge-negative" : tone === "regulatory" ? "badge-regulatory" : tone === "ink" ? "badge-ink" : ""}`}>
        {children}
    </span>
);

const Section = ({ title, children, defaultOpen = true, eyebrow, testId }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <section className="border-t border-[hsl(var(--line))] pt-4 mt-4" data-testid={testId}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full text-left flex items-center justify-between gap-3 group"
            >
                <div>
                    {eyebrow && <div className="eyebrow">{eyebrow}</div>}
                    <div className="text-sm font-semibold text-[hsl(var(--ink))] mt-0.5 leading-snug">{title}</div>
                </div>
                <span className="text-xs text-[hsl(var(--mute))] group-hover:text-[hsl(var(--ink))]">{open ? "−" : "+"}</span>
            </button>
            {open && <div className="mt-3">{children}</div>}
        </section>
    );
};

const Stat = ({ label, value }) => (
    <div className="flex flex-col">
        <div className="eyebrow">{label}</div>
        <div className="text-base font-semibold num text-[hsl(var(--ink))] leading-tight">{value ?? "—"}</div>
    </div>
);

const ExpandableText = ({ text, className, clampLines = 3, moreLabel = "Read more", lessLabel = "Show less", testId }) => {
    const [open, setOpen] = useState(false);
    if (!text) return null;
    const style = open ? undefined : { display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: String(clampLines), overflow: "hidden" };
    return (
        <div data-testid={testId}>
            <p className={className} style={style}>{text}</p>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="text-[11px] font-medium text-[hsl(var(--accent-ink))] hover:underline mt-1"
                data-testid={testId ? `${testId}-toggle` : undefined}
            >
                {open ? lessLabel : moreLabel}
            </button>
        </div>
    );
};

const SourceRow = ({ source, testId }) => {
    const primary = source.title || source.publisher || source.url || "—";
    const dateLabel = (source.publishedAt || "").slice(0, 10);
    const evidenceParts = [];
    if (typeof source.companyCount === "number" && source.companyCount > 0) evidenceParts.push(`companies ${source.companyCount}`);
    if (typeof source.assetClassCount === "number" && source.assetClassCount > 0) evidenceParts.push(`assets ${source.assetClassCount}`);
    if (typeof source.blockchainCount === "number" && source.blockchainCount > 0) evidenceParts.push(`chains ${source.blockchainCount}`);
    const relevance = typeof source.relevanceScore === "number" ? Math.round(source.relevanceScore * 10) / 10 : null;
    return (
        <li className="text-xs leading-snug" data-testid={testId}>
            {source.url ? (
                <a href={source.url} target="_blank" rel="noreferrer" title={source.url} className="text-[hsl(var(--accent-ink))] hover:underline font-medium block truncate">{primary}</a>
            ) : (
                <span className="text-[hsl(var(--ink-soft))] block">{primary}</span>
            )}
            <div className="text-[11px] text-[hsl(var(--mute))] mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                {source.publisher && <span>{source.publisher}</span>}
                {source.publisher && dateLabel && <span aria-hidden>·</span>}
                {dateLabel && <span className="num">{dateLabel}</span>}
                {source.sourceType && <><span aria-hidden className="opacity-60">·</span><span className="text-[10px] uppercase tracking-wider">{source.sourceType.replace(/_/g, " ").toLowerCase()}</span></>}
                {evidenceParts.length > 0 && <><span aria-hidden className="opacity-60">·</span><span className="text-[10px] uppercase tracking-wider">{evidenceParts.join(" · ")}</span></>}
                {relevance !== null && <><span aria-hidden className="opacity-60">·</span><span className="text-[10px] uppercase tracking-wider" title="Relevance score">rel {relevance}</span></>}
            </div>
        </li>
    );
};

const TopSourcesBlock = ({ primary, secondary, primaryLabel }) => {
    const [showAll, setShowAll] = useState(false);
    const allByUrl = new Map();
    for (const s of primary || []) allByUrl.set(s.url, s);
    for (const s of secondary || []) if (!allByUrl.has(s.url)) allByUrl.set(s.url, s);
    const all = [...allByUrl.values()];
    if (all.length === 0) return null;
    const view = showAll ? all : all.slice(0, 3);
    return (
        <div className="mt-4">
            <div className="flex items-baseline justify-between gap-2">
                <div className="eyebrow">{primaryLabel ? `Top sources · ${primaryLabel.toLowerCase()}` : "Top sources"}</div>
                <span className="text-[10px] text-[hsl(var(--mute))] num">{showAll ? all.length : Math.min(3, all.length)} / {all.length}</span>
            </div>
            <ul className="space-y-2.5 mt-2" data-testid="top-sources-list">
                {view.map((s, i) => <SourceRow key={s.url || i} source={s} testId={`source-card-${i}`} />)}
            </ul>
            {all.length > 3 && (
                <button
                    type="button"
                    onClick={() => setShowAll((v) => !v)}
                    data-testid="top-sources-toggle"
                    className="text-[11px] font-medium text-[hsl(var(--accent-ink))] hover:underline mt-2"
                >
                    {showAll ? "Show fewer" : `Show all ${all.length} sources`}
                </button>
            )}
        </div>
    );
};

const MarketTrackDetailPanel = ({ track, history, totalWeeks, subPatterns, relatedTracks, allTracks, onSelectTrack, latestSignal, briefingFeature }) => {
    const sortedHistory = useMemo(() => sortHistoryAsc(history || []), [history]);
    const latestWeek = sortedHistory.length ? sortedHistory[sortedHistory.length - 1] : null;
    const [framingOpen, setFramingOpen] = useState(false);
    const panelRef = useRef(null);

    // When the user clicks `Inspect →` on a nested sub-pattern or weekly
    // movement, hand off to the parent's `onSelectTrack` (which swaps the
    // selected track and triggers a re-render of this panel) and then snap
    // the viewport back to the top of the panel so the user lands on the
    // freshly-selected track's identity row instead of wherever the click
    // happened (typically deep in the panel near the footer).
    const handleInlineSelect = (id) => {
        if (!onSelectTrack || !id) return;
        onSelectTrack(id);
        requestAnimationFrame(() => {
            if (panelRef.current && typeof panelRef.current.scrollIntoView === "function") {
                panelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        });
    };

    const [pickedWeekId, setPickedWeekId] = useState(null);
    useEffect(() => {
        // when track changes, snap to the latest week by default
        setPickedWeekId(latestWeek ? latestWeek.TrackWeekID : null);
    }, [track && track.TrackID, latestWeek && latestWeek.TrackWeekID]);

    const pickedWeek = useMemo(() => {
        if (!pickedWeekId) return latestWeek;
        return sortedHistory.find((h) => h.TrackWeekID === pickedWeekId) || latestWeek;
    }, [pickedWeekId, sortedHistory, latestWeek]);

    // Collapse the "Read full weekly framing" expand whenever the user
    // changes the selected week. Without this, framingOpen could leak
    // across week selections — e.g. expand on the latest week, then click
    // an older week → if the rich-expand path were still active, the body
    // would belong to a different week than the header. Also resets on
    // track change for symmetry.
    useEffect(() => {
        setFramingOpen(false);
    }, [track && track.TrackID, pickedWeekId]);

    // Use the rich source layer when the latest signal row carries it; fall
    // back to the parsed-from-history `TopSourcesJSON` otherwise.
    const sourceLists = useMemo(() => collectSourceLists(latestSignal || pickedWeek), [latestSignal, pickedWeek]);

    // Actors-with-counts derived from the latest signal row distributions.
    const actorCompanies = useMemo(
        () => parseDistribution(latestSignal?.weightedCompanyDistribution || latestSignal?.companyDistribution),
        [latestSignal]
    );
    const actorCompanyTypes = useMemo(
        () => parseDistribution(latestSignal?.weightedCompanyTypeDistribution || latestSignal?.companyTypeDistribution),
        [latestSignal]
    );
    const actorAssetClasses = useMemo(
        () => parseDistribution(latestSignal?.assetClassDistribution),
        [latestSignal]
    );
    const actorBlockchains = useMemo(
        () => parseDistribution(latestSignal?.blockchainDistribution),
        [latestSignal]
    );

    if (!track) {
        return (
            <aside className="card-flat p-6" data-testid="track-detail-panel">
                <div className="eyebrow">Market signal</div>
                <p className="text-sm text-[hsl(var(--mute))] mt-3 leading-relaxed">Select a signal from the board to see its thesis, evidence and weekly history.</p>
            </aside>
        );
    }

    const observed = track.WeeksObserved || sortedHistory.length || 0;
    const persistencePct = track.PersistenceRatio != null
        ? Math.round(track.PersistenceRatio * 100)
        : (totalWeeks ? Math.round((observed / totalWeeks) * 100) : null);

    const evidenceFromTrack = track.LatestEvidenceJSON || null;

    // New v5: `marketTrack.latestWeeklyEvidence`. When present, drives the
    // expanded "Read full weekly framing" body strictly from
    // `latestWeeklyEvidence.summary` + `latestWeeklyEvidence.whyItMatters`
    // (NEVER from `WeeklyHistoryJSON[].WeeklyFraming` / `Summary`, the
    // legacy `LatestEvidenceJSON.summary`, or any truncated preview field).
    // When absent, the legacy `pickedWeek.WeeklyFraming` rendering is
    // preserved exactly as before for backward compatibility.
    //
    // `latestWeeklyEvidence` (lwe) is still read here for evidence chips,
    // source metadata, and per-claim chip resolution elsewhere in the
    // panel. It is NO LONGER the source for the expanded weekly framing
    // body — that role is owned by the selected weekly-history row's
    // `Summary`, which the Publisher now enriches with the best
    // available per-week narrative (including canonicalized combined
    // summaries for alias rollups). This keeps the expand state purely
    // presentational: collapsed and expanded ALWAYS read from the same
    // row, on ALL weeks (latest or historical).
    const lwe = readLatestWeeklyEvidence(track);
    const pickedSummary = (typeof pickedWeek?.Summary === "string") ? pickedWeek.Summary.trim() : "";
    const pickedSummaryAppearsComplete = summaryAppearsComplete(pickedSummary);
    const canReadFullFraming = pickedSummaryAppearsComplete && pickedSummary.length > 900;
    const resolvedExpandedBody = canReadFullFraming ? pickedWeek.Summary : null;
    const lweSources = lwe ? pickLatestEvidenceSources(lwe) : [];
    // Example-chip priority for the latest-evidence chip strip:
    //   topCompanies → topAssetClasses → topLocations.
    const lweChipBundle = lwe ? {
        examples: [],
        companies: lwe.topCompanies,
        locations: lwe.topLocations,
        assetClasses: lwe.topAssetClasses,
        sourceLinks: lweSources,
        sourceCount: lwe.sourceCount || lweSources.length,
        exampleCount: (lwe.topCompanies || []).length,
    } : null;

    const hasActorContext = actorCompanies.length || actorCompanyTypes.length || actorAssetClasses.length || actorBlockchains.length;

    return (
        <aside ref={panelRef} className="card-flat p-6 space-y-2" data-testid="track-detail-panel">
            <div className="eyebrow">Market signal</div>
            <h2 className="display text-2xl leading-snug text-[hsl(var(--ink))]" data-testid="track-detail-title">{track.StableTrackName}</h2>
            {track.SuggestedDisplayLabel && track.SuggestedDisplayLabel !== track.StableTrackName && (
                <div className="text-xs text-[hsl(var(--mute))] italic">{track.SuggestedDisplayLabel}</div>
            )}
            {briefingFeature && (
                <div className="mt-1.5" data-testid="briefing-feature-chip">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border border-[hsl(var(--accent))] bg-[hsl(var(--accent-soft))] text-[hsl(var(--accent-ink))]">
                        <span aria-hidden>★</span>
                        Featured in this week's Executive Market Read
                        {briefingFeature.kind === "regulatory" && <span className="text-[hsl(var(--mute))]">· Regulatory watch</span>}
                        {briefingFeature.kind === "top" && <span className="text-[hsl(var(--mute))]">· Top signal</span>}
                    </span>
                </div>
            )}
            {/* Header chip row — fixed order:
                1. Primary role chip (Core / Watchlist / Sub-pattern / Weekly movement)
                2. Lifecycle (active / dormant / stale)
                3. Regulatory split (regulatory / non-regulatory)
                4. Evidence quality (replaces the previous conviction chip;
                   conviction is already conveyed by the signal table column
                   and a duplicate here cluttered the header).
                5. Market zone (contextual classification — last so the
                   evidence interpretation stays close to the signal state).
                Strategic-urgency chip remains intentionally hidden. */}
            <div className="flex flex-wrap items-center gap-2 mt-2">
                {(() => {
                    // Single primary role chip — no second "Track" status chip.
                    const chip = primaryRoleChipForTrack(track);
                    return chip ? <Badge tone={chip.tone}>{chip.label}</Badge> : null;
                })()}
                {track.LifecycleStatus && <Badge tone={lifecycleTone(track.LifecycleStatus)}>{track.LifecycleStatus.toLowerCase()}</Badge>}
                {(() => {
                    // MaturityStage sometimes carries a role-like value that
                    // duplicates the primary role chip — e.g. "Core Track",
                    // "Watchlist Track", "Weekly Dynamic", "Weekly Movement",
                    // "Sub-pattern". Suppress those redundant values and only
                    // render real maturity-stage names.
                    const m = track.MaturityStage;
                    if (!m) return null;
                    const looksLikeRoleChip = /\b(track|signal|pattern|dynamic|movement)\b/i.test(String(m).trim());
                    if (looksLikeRoleChip) return null;
                    return <Badge tone="muted">{m}</Badge>;
                })()}
                {track.RegulatorySplit && <Badge tone={track.RegulatorySplit === "REGULATORY" ? "regulatory" : "muted"}>{track.RegulatorySplit.toLowerCase().replace(/_/g, "-")}</Badge>}
                {(() => {
                    const eq = evidenceQualityLabelForTrack(track);
                    return eq ? <Badge tone="muted">{eq}</Badge> : null;
                })()}
                {(() => {
                    // User-facing market zone chip. Falls back to legacy MacroTheme.
                    const mz = marketZoneForTrack(track);
                    return mz ? <Badge tone="muted">{mz}</Badge> : null;
                })()}
            </div>

            {/* `governance.signature` / `SignatureOneSentence` remain
                available in the data model but are intentionally NOT
                rendered as a separate `Signal definition` section in v1 —
                the existing track thesis / current evidence sections
                cover this need and adding a second definition was
                confusing. Re-enable in a later iteration if needed. */}

            {track.TrackThesis && (
                <p className="text-sm leading-relaxed text-[hsl(var(--ink-soft))] mt-4 max-w-2xl whitespace-pre-line" data-testid="track-thesis">{track.TrackThesis}</p>
            )}

            {track.MarketRelevance && (
                <Section title="Why this matters" eyebrow="Market relevance" defaultOpen testId="section-market-relevance">
                    <p className="text-sm leading-relaxed text-[hsl(var(--ink-soft))] whitespace-pre-line">{track.MarketRelevance}</p>
                </Section>
            )}

            {/* Latest evidence */}
            <Section title={`Latest weekly framing · ${pickedWeek?.YearWeek || track.LastSeenYearWeek || "—"}`} eyebrow="Current evidence" defaultOpen testId="section-latest-evidence">
                {pickedWeek?.WeeklyFraming && !canReadFullFraming && (
                    // Older weeks — the publisher does NOT ship a per-week
                    // full body (history rows only carry a truncated
                    // `Summary` of ~901 chars ending in "..." plus the
                    // 49-char `WeeklyFraming` headline). Render the
                    // headline statically and do NOT expose a misleading
                    // "Read full weekly framing →" toggle that cannot
                    // deliver. The truncated `picked-week-summary`
                    // paragraph below still surfaces the available body.
                    <p
                        className="text-sm font-medium text-[hsl(var(--ink))] leading-snug"
                        data-testid="latest-framing-title"
                    >
                        {pickedWeek.WeeklyFraming}
                    </p>
                )}
                {pickedWeek?.WeeklyFraming && canReadFullFraming && (
                    <>
                        <p className="text-sm font-medium text-[hsl(var(--ink))] leading-snug" data-testid="latest-framing-title">{pickedWeek.WeeklyFraming}</p>
                        <button
                            type="button"
                            data-testid="track-read-full-framing"
                            onClick={() => {
                                const nextOpen = !framingOpen;
                                setFramingOpen(nextOpen);
                                trackEvent(nextOpen ? "market_framing_expand" : "market_framing_collapse", {
                                    track_id: track?.TrackID || "",
                                    year_week: pickedWeek?.YearWeek || "",
                                    body_length: resolvedExpandedBody ? resolvedExpandedBody.length : 0,
                                });
                            }}
                            aria-expanded={framingOpen}
                            className="text-[11px] font-medium text-[hsl(var(--accent-ink))] hover:underline mt-1.5"
                        >
                            {framingOpen ? "← Show less" : "Read full weekly framing →"}
                        </button>
                        {framingOpen && (
                            <div className="mt-2" data-testid="track-full-framing">
                                {resolvedExpandedBody && (
                                    <p className="text-sm text-[hsl(var(--ink))] leading-relaxed whitespace-pre-line" data-testid="track-full-framing-summary">{resolvedExpandedBody}</p>
                                )}
                                {/* Note: lwe.whyItMatters intentionally NOT rendered here. The
                                    same content is already shown as the italic interpretation
                                    below the metric row (see `picked-week-why` below) — rendering
                                    it inside the expanded framing would duplicate it. */}
                            </div>
                        )}
                    </>
                )}
                {pickedWeek?.Summary && (!canReadFullFraming || !framingOpen) && (
                    <p
                        className="text-xs text-[hsl(var(--ink-soft))] leading-relaxed mt-2 whitespace-pre-line"
                        // When a "Read full weekly framing →" toggle is offered
                        // (i.e. there is genuinely more text to reveal on
                        // expand), render the collapsed preview clamped to 3
                        // lines so expand/collapse provides a real visual
                        // difference. When no expand toggle exists, keep the
                        // full Summary visible — clamping then would hide
                        // content the user has no way to recover.
                        style={canReadFullFraming ? { display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: "3", overflow: "hidden" } : undefined}
                        data-testid="picked-week-summary"
                    >{pickedWeek.Summary}</p>
                )}
                {lweChipBundle && (lweChipBundle.sourceCount > 0 || (lweChipBundle.companies || []).length > 0 || (lweChipBundle.locations || []).length > 0) && (
                    <EvidenceChips
                        evidence={lweChipBundle}
                        sources={lweSources}
                        sourceCount={lweChipBundle.sourceCount}
                        testIdScope="track-latest-evidence"
                        maxChips={3}
                        dense
                    />
                )}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                    <Stat label="Score" value={pickedWeek?.Score ?? track.LatestScore} />
                    <Stat label="Articles" value={pickedWeek?.Articles ?? track.LatestArticleCount} />
                    <Stat label="Sources" value={pickedWeek?.Sources ?? track.LatestSourceCount} />
                    <Stat label="Companies" value={pickedWeek?.Companies ?? track.LatestCompanyBreadth} />
                </div>
                {pickedWeek?.WhyItMatters && (
                    <div className="mt-3 border-l-2 border-[hsl(var(--accent))] pl-3 text-xs italic text-[hsl(var(--ink-soft))] leading-relaxed" data-testid="picked-week-why">{pickedWeek.WhyItMatters}</div>
                )}
                <TopSourcesBlock primary={sourceLists.primary} secondary={sourceLists.secondary} primaryLabel={sourceLists.primaryLabel} />
                {!pickedWeek && evidenceFromTrack && (
                    <p className="text-xs text-[hsl(var(--mute))] mt-2 leading-relaxed">
                        Evidence summary unavailable for the latest indexed week. {evidenceFromTrack.weeklyFraming || ""}
                    </p>
                )}
            </Section>

            {/* This week's evidence (latest snapshot) */}
            {hasActorContext && (
                <Section title="This week’s evidence" eyebrow="Latest evidence" defaultOpen testId="section-actors-shaping">
                    <p className="text-[11px] text-[hsl(var(--mute))] mb-3 leading-relaxed" data-testid="latest-evidence-helper">
                        Actors, asset classes and chains visible in the latest weekly evidence.
                    </p>
                    {actorCompanies.length > 0 && (
                        <div data-testid="actors-companies">
                            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium mb-1.5">Companies</div>
                            <RankedList items={actorCompanies} max={5} testId="ranked-companies" />
                        </div>
                    )}
                    {actorCompanyTypes.length > 0 && (
                        <div className="mt-3" data-testid="actors-company-types">
                            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium mb-1.5">Company types</div>
                            <RankedList items={actorCompanyTypes} max={6} testId="ranked-company-types" />
                        </div>
                    )}
                    {actorAssetClasses.length > 0 && (
                        <div className="mt-3" data-testid="actors-asset-classes">
                            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium mb-1.5">Asset classes</div>
                            <RankedList items={actorAssetClasses} max={6} testId="ranked-asset-classes" />
                        </div>
                    )}
                    {actorBlockchains.length > 0 && (
                        <div className="mt-3" data-testid="actors-blockchains">
                            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium mb-1.5">Blockchains</div>
                            <RankedList items={actorBlockchains} max={6} testId="ranked-blockchains" />
                        </div>
                    )}
                </Section>
            )}

            {/* Track composition — full lifetime structural composition (companies,
                company types, asset classes, blockchains, locations) from the new
                `trackLevelCounts` payload. Renders only when at least one dimension
                has usable data. */}
            <TrackComposition counts={track.trackLevelCounts} />

            {/* Latest movement — what changed for this signal this week */}
            {latestSignal && <LatestMovement signal={latestSignal} />}

            {/* Track over time */}
            {sortedHistory.length > 0 && (
                <Section title={`Signal history · ${sortedHistory.length} weeks`} eyebrow="Timeline" defaultOpen testId="section-track-timeline">
                    <p className="text-xs text-[hsl(var(--mute))] mb-2 leading-relaxed">Click any week to inspect its weekly framing, summary and top sources.</p>
                    <div className="overflow-x-auto -mx-2">
                        <table className="w-full text-xs">
                            <thead className="bg-[hsl(var(--surface-sunken))]">
                                <tr>
                                    <th className="px-2 py-1.5 text-[10px] font-medium text-[hsl(var(--mute))] uppercase tracking-wider text-left">Week</th>
                                    <th className="px-2 py-1.5 text-[10px] font-medium text-[hsl(var(--mute))] uppercase tracking-wider text-left">Visibility</th>
                                    <th className="px-2 py-1.5 text-[10px] font-medium text-[hsl(var(--mute))] uppercase tracking-wider text-right">Score</th>
                                    <th className="px-2 py-1.5 text-[10px] font-medium text-[hsl(var(--mute))] uppercase tracking-wider text-right">Articles</th>
                                    <th className="px-2 py-1.5 text-[10px] font-medium text-[hsl(var(--mute))] uppercase tracking-wider text-right">Sources</th>
                                    <th className="px-2 py-1.5 text-[10px] font-medium text-[hsl(var(--mute))] uppercase tracking-wider text-right">Companies</th>
                                    <th className="px-2 py-1.5 text-[10px] font-medium text-[hsl(var(--mute))] uppercase tracking-wider text-left max-w-[180px]">Weekly framing</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...sortedHistory].reverse().map((h) => {
                                    const active = h.TrackWeekID === pickedWeekId;
                                    return (
                                        <tr
                                            key={h.TrackWeekID}
                                            data-testid={`timeline-row-${h.TrackWeekID}`}
                                            onClick={() => {
                                                setPickedWeekId(h.TrackWeekID);
                                                trackEvent("market_week_pick", {
                                                    track_id: track?.TrackID || "",
                                                    year_week: h.YearWeek || h.Week || "",
                                                    is_latest: latestWeek ? h.TrackWeekID === latestWeek.TrackWeekID : false,
                                                });
                                            }}
                                            className={`border-b border-[hsl(var(--line))] cursor-pointer ${active ? "bg-[hsl(var(--accent-soft))]" : "hover:bg-[hsl(var(--surface-sunken))]"}`}
                                        >
                                            <td className="px-2 py-1.5 num">{h.YearWeek || h.Week}</td>
                                            <td className="px-2 py-1.5"><Badge tone={visibilityTone(h.Visibility)}>{h.Visibility || "—"}</Badge></td>
                                            <td className="px-2 py-1.5 text-right num">{h.Score ?? "—"}</td>
                                            <td className="px-2 py-1.5 text-right num">{h.Articles ?? "—"}</td>
                                            <td className="px-2 py-1.5 text-right num">{h.Sources ?? "—"}</td>
                                            <td className="px-2 py-1.5 text-right num">{h.Companies ?? "—"}</td>
                                            <td className="px-2 py-1.5 text-[hsl(var(--ink-soft))] truncate max-w-[180px]" title={h.WeeklyFraming}>{h.WeeklyFraming || "—"}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Section>
            )}

            {/* Persistence + breadth — focused on persistence, scope and structure.
                Actor / asset composition lives in <TrackComposition /> above. */}
            <Section title="Signal footprint" eyebrow="Structural footprint" defaultOpen={false} testId="section-track-footprint">
                <p className="text-[11px] text-[hsl(var(--mute))] mb-3 leading-relaxed" data-testid="footprint-helper">
                    Persistence, maturity and governance of this signal.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Stat label="Weeks observed" value={persistencePct != null ? `${observed}${totalWeeks ? `/${totalWeeks}` : ""} (${persistencePct}%)` : observed} />
                    <Stat label="Signal weeks" value={track.SignalWeeks} />
                    <Stat label="Watchlist weeks" value={track.WatchlistWeeks} />
                    <Stat label="Avg score" value={track.AvgScore} />
                </div>
            </Section>

            {track.ScopeDefinition && (
                <Section title="Scope" eyebrow="Scope definition" defaultOpen={false} testId="section-scope">
                    <p className="text-xs text-[hsl(var(--ink-soft))] leading-relaxed whitespace-pre-line">{track.ScopeDefinition}</p>
                </Section>
            )}
            {track.Exclusions && (
                <Section title="Exclusions" eyebrow="Out of scope" defaultOpen={false} testId="section-exclusions">
                    <p className="text-xs text-[hsl(var(--ink-soft))] leading-relaxed whitespace-pre-line">{track.Exclusions}</p>
                </Section>
            )}

            {(() => {
                // v5 governed-signal-architecture: weekly dynamics live in a
                // sibling list. Falls back to `[]` on legacy JSONs so this
                // entire block collapses to a no-op there.
                const weekly = weeklyDynamicsFor(track, allTracks);
                const hasSubPatterns = subPatterns && subPatterns.length > 0;
                const hasWeekly = weekly && weekly.length > 0;
                if (!hasSubPatterns && !hasWeekly) return null;
                return (
                    <Section title="Signal structure" eyebrow="Underlying evidence clusters" defaultOpen testId="section-signal-structure">
                        {hasSubPatterns && (
                            <div data-testid="signal-structure-sub-patterns">
                                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium mb-1.5">Related sub-patterns · {subPatterns.length}</div>
                                <ul className="space-y-2">
                                    {subPatterns.map((s) => {
                                        // Short human-readable description for the card body.
                                        // Prefer the existing track thesis (1–2 sentences),
                                        // then fall back to the latest weekly framing. We
                                        // deliberately do NOT render `CuratedReviewNote` /
                                        // governance debug strings (decision=, rule=,
                                        // role=, parent=, Reason: …) which sometimes leak
                                        // through from upstream curation tooling.
                                        const desc = (typeof s.TrackThesis === "string" && s.TrackThesis.trim()) ? s.TrackThesis.trim() : null;
                                        return (
                                            <li key={s.TrackID} className="card-sunken p-3" data-testid={`sub-pattern-${s.TrackID}`}>
                                                <div className="flex items-start justify-between gap-2 flex-wrap">
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-sm font-medium text-[hsl(var(--ink))] leading-snug">{s.StableTrackName}</div>
                                                        {desc && <div className="text-[11px] text-[hsl(var(--ink-soft))] mt-1 leading-snug line-clamp-2">{desc}</div>}
                                                        {s.LatestWeeklyFraming && <div className="text-[11px] text-[hsl(var(--mute))] mt-1 leading-snug line-clamp-2 italic">{s.LatestWeeklyFraming}</div>}
                                                        <div className="text-[11px] text-[hsl(var(--ink-soft))] mt-1 num">
                                                            {(s.WeeksObserved ?? 0)} weeks observed · score {s.LatestScore ?? "—"} · {s.LatestVisibility || "—"}
                                                        </div>
                                                    </div>
                                                    {onSelectTrack && (
                                                        <button type="button" onClick={() => handleInlineSelect(s.TrackID)} className="text-[11px] text-[hsl(var(--accent-ink))] hover:underline whitespace-nowrap">Inspect →</button>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                        {hasWeekly && (
                            <div className={hasSubPatterns ? "mt-4" : ""} data-testid="signal-structure-weekly-movements">
                                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium mb-1.5">Current weekly movements · {weekly.length}</div>
                                <ul className="space-y-2">
                                    {weekly.map((w) => {
                                        const eq = evidenceQualityLabelForTrack(w);
                                        return (
                                            <li key={w.TrackID} className="card-sunken p-3" data-testid={`weekly-dynamic-${w.TrackID}`}>
                                                <div className="flex items-start justify-between gap-2 flex-wrap">
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-sm font-medium text-[hsl(var(--ink))] leading-snug">{w.StableTrackName}</div>
                                                        {w.LatestWeeklyFraming && <div className="text-[11px] text-[hsl(var(--ink-soft))] mt-1 leading-snug line-clamp-2">{w.LatestWeeklyFraming}</div>}
                                                        <div className="text-[11px] text-[hsl(var(--ink-soft))] mt-1 flex items-center gap-1.5 flex-wrap num">
                                                            {typeof w.LatestScore === "number" && <span>score {w.LatestScore}</span>}
                                                            {w.LatestVisibility && <span>· {w.LatestVisibility}</span>}
                                                            {eq && <Badge tone="muted">{eq}</Badge>}
                                                        </div>
                                                    </div>
                                                    {onSelectTrack && (
                                                        <button type="button" onClick={() => handleInlineSelect(w.TrackID)} className="text-[11px] text-[hsl(var(--accent-ink))] hover:underline whitespace-nowrap">Inspect →</button>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                    </Section>
                );
            })()}

            {relatedTracks && relatedTracks.length > 0 && (
                <Section title={`Adjacent market signals · ${relatedTracks.length}`} eyebrow="Related" defaultOpen={false} testId="section-related-tracks">
                    <ul className="space-y-1.5">
                        {relatedTracks.map((r) => (
                            <li key={r.track.TrackID} className="flex items-start gap-2 text-sm" data-testid={`related-track-${r.track.TrackID}`}>
                                <Badge tone="muted">{r.reason}</Badge>
                                <button type="button" onClick={() => handleInlineSelect(r.track.TrackID)} className="text-[hsl(var(--ink))] hover:text-[hsl(var(--accent-ink))] hover:underline flex-1 text-left leading-snug">{r.track.StableTrackName}</button>
                            </li>
                        ))}
                    </ul>
                </Section>
            )}
        </aside>
    );
};

export default MarketTrackDetailPanel;
